import type { TileId } from './types';
import { AtAGlanceTile } from './tiles/AtAGlanceTile';
import { UncertaintyTile } from './tiles/UncertaintyTile';
import { TimelineTile } from './tiles/TimelineTile';
import { EvolutionTile } from './tiles/EvolutionTile';
import { MapTile } from './tiles/MapTile';
import type { MapTableInsetRow } from '@/components/maps/insets';
import type { VariableOption } from '@/components/maps/insets/VariableSelectionDropdown';
import type { StormImpactModel } from '@/types';

export interface SIPTileProps {
  // Shared data (AtAGlance + Uncertainty)
  tableData: MapTableInsetRow[];
  aggregationRow: MapTableInsetRow | null;
  availableVariables: VariableOption[];
  selectedVariables: string[];
  onSelectedVariablesChange: (variables: string[]) => void;
  csvMetadata: {
    weatherModel?: string;
    validDate?: Date | null;
    modelRunDate?: Date | null;
    predictionType?: string;
  };
  tableLoading: boolean;
  bestEstimateVariableUnits: string;
  effectiveVariableUnits: string;
  // Timeline + Evolution props
  nodeId?: string;
  modelId?: string;
  weatherModelId?: string;
  startDate?: Date;
  forecastRange?: number;
  run?: string;
  /** From available-runs — Evolution uses these as the run source of truth. */
  availableRuns?: string[];
  availableRunsLoading?: boolean;
  /**
   * When false, Evolution waits (no fan-out requests) so Timeline/map/grid
   * can finish after model/date/run changes.
   */
  evolutionEnabled?: boolean;
  /** Leaf nodes under selected geo scope — scales Evolution fan-out. */
  scopeLeafCount?: number;
  stormImpactModel?: StormImpactModel;
  // Map props
  mapInteraction: any;
  filters: any;
  leafHintVisible?: boolean;
}

interface SIPMosaicTileRendererProps {
  id: TileId;
  tileProps: SIPTileProps;
}

export function SIPMosaicTileRenderer({ id, tileProps }: SIPMosaicTileRendererProps) {
  switch (id) {
    case 'at-a-glance':
      return (
        <AtAGlanceTile
          data={tileProps.tableData}
          aggregationRow={tileProps.aggregationRow}
          availableVariables={tileProps.availableVariables}
          selectedVariables={tileProps.selectedVariables}
          onSelectedVariablesChange={tileProps.onSelectedVariablesChange}
          csvMetadata={tileProps.csvMetadata}
          loading={tileProps.tableLoading}
        />
      );
    case 'uncertainty':
      return (
        <UncertaintyTile
          data={tileProps.tableData}
          yAxisTitle={tileProps.bestEstimateVariableUnits}
        />
      );
    case 'timeline':
      return (
        <TimelineTile
          nodeId={tileProps.nodeId}
          modelId={tileProps.modelId}
          weatherModelId={tileProps.weatherModelId}
          startDate={tileProps.startDate}
          forecastRange={tileProps.forecastRange}
          run={tileProps.run}
          stormImpactModel={tileProps.stormImpactModel}
          units={tileProps.bestEstimateVariableUnits}
        />
      );
    case 'evolution':
      return (
        <EvolutionTile
          nodeId={tileProps.nodeId}
          modelId={tileProps.modelId}
          weatherModelId={tileProps.weatherModelId}
          startDate={tileProps.startDate}
          availableRuns={tileProps.availableRuns}
          availableRunsLoading={tileProps.availableRunsLoading}
          enabled={tileProps.evolutionEnabled}
          scopeLeafCount={tileProps.scopeLeafCount}
          stormImpactModel={tileProps.stormImpactModel}
          units={tileProps.bestEstimateVariableUnits}
        />
      );
    case 'map':
      return (
        <MapTile
          mapInteraction={tileProps.mapInteraction}
          filters={tileProps.filters}
          effectiveVariableUnits={tileProps.effectiveVariableUnits}
          leafHintVisible={tileProps.leafHintVisible}
        />
      );
    default:
      return null;
  }
}
